import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment-types'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import type {ActionListItemProps as ItemProps} from '@primer/react/deprecated'
import {useCallback} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {SuggestedAssignee} from '../../api/memex-items/contracts'
import {useCopilotAssignmentModal} from '../../components/react_table/copilot-assignment-modal-provider'
import {isCopilot} from '../../helpers/assignee-helper'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useFetchSuggestedAssignees} from '../../state-providers/suggestions/use-fetch-suggested-assignees'
import {useUpdateItem} from '../use-update-item'
import styles from './use-assignees-editor.module.css'

export const getLogin = (option: SuggestedAssignee) => {
  return option.login
}

export const convertOptionToItem = (
  option: SuggestedAssignee,
  selectedAssignees: Array<SuggestedAssignee>,
): ItemProps & SuggestedAssignee => {
  const selected = selectedAssignees.find(assignee => assignee.id === option.id)
  return {
    ...option,
    leadingVisual() {
      return isCopilot(option.login) ? (
        <CopilotAvatar className={styles.GitHubAvatar} />
      ) : (
        <GitHubAvatar alt={option.login} src={option.avatarUrl} className={styles.GitHubAvatar} />
      )
    },
    text: option.login,
    description: option.name ?? undefined,
    descriptionVariant: 'inline',
    groupId: selected ? 'assigned' : 'suggestions',
    ...testIdProps('table-cell-editor-row'),
  }
}

type UseAssigneesEditorProps = {
  model: MemexItemModel
}

type UseAssigneesEditorReturn = {
  fetchOptions: () => void
  saveSelected: (nextSelected: Array<SuggestedAssignee>) => Promise<void>
}

export function useAssigneesEditor({model}: UseAssigneesEditorProps): UseAssigneesEditorReturn {
  const {updateItem} = useUpdateItem()
  const {fetchSuggestedAssignees} = useFetchSuggestedAssignees()
  const {openModal} = useCopilotAssignmentModal()

  const crossRepoAssignEnabled = isFeatureEnabled('issues_copilot_cross_repo_assign')

  const fetchOptions = useCallback(() => {
    fetchSuggestedAssignees(model)
  }, [model, fetchSuggestedAssignees])

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedAssignee>) => {
      const copilotAssignee = nextSelected.find(assignee => isCopilot(assignee.login))
      const nonCopilotAssignees = nextSelected.filter(assignee => !isCopilot(assignee.login))
      const copilotAlreadyAssigned = model.getAssignees().some(assignee => isCopilot(assignee.login))

      const nameWithOwner = model.getNameWithOwner()
      const issueNumber = model.getItemNumber()

      const shouldOpenModal = Boolean(
        !copilotAlreadyAssigned &&
          copilotAssignee &&
          crossRepoAssignEnabled &&
          nameWithOwner &&
          nameWithOwner.length === 2 &&
          issueNumber,
      )

      if (shouldOpenModal) {
        // the typecast here is safe-guarded by the length check above
        const [owner, repo] = nameWithOwner as [string, string]

        const repository: CopilotAssignmentRepository = {
          name: repo,
          nameWithOwner: `${owner}/${repo}`,
          databaseId: model.contentRepositoryId,
          owner: {
            login: owner,
          },
        }

        const onSuccess = async () => {
          await updateItem(model, {
            dataType: MemexColumnDataType.Assignees,
            value: nextSelected,
          })
        }

        openModal(repository, issueNumber, onSuccess)
      }

      // Normal assignment flow (no Copilot involved)
      await updateItem(model, {
        dataType: MemexColumnDataType.Assignees,
        value: shouldOpenModal ? nonCopilotAssignees : nextSelected,
      })
    },
    [model, updateItem, crossRepoAssignEnabled, openModal],
  )

  return {
    fetchOptions,
    saveSelected,
  }
}
